import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Image,
  ActivityIndicator,
} from 'react-native';
import { useMutation } from '@tanstack/react-query';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Check } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useAlert } from '@/components/AlertDialog';
import { AuthLayout } from '@/components/AuthLayout';
import { isWeb } from '@/lib/platform';
import { useAuth, isSupabaseConfigured } from '@/lib/supabase';
import { getUserMessage } from '@/lib/errors';
import AnimatedPressable from '@/components/AnimatedPressable';

import { LOGO_MARK } from '@/lib/logo';

type ScreenState = 'loading' | 'input' | 'success' | 'error';

/**
 * How long to wait for Supabase to emit `PASSWORD_RECOVERY` after this screen
 * mounts.
 *
 * On web, supabase-js (`detectSessionInUrl`) parses the recovery fragment and
 * emits the event asynchronously, so a decision on first render would always
 * be "no". The previous code waited a flat 1s and then accepted any session;
 * we wait a little longer — the cost of being generous here is only a spinner,
 * whereas being impatient rejects a legitimate recovery — and unlock the
 * instant the event arrives.
 */
const RECOVERY_EVENT_GRACE_MS = 3000;

interface PasswordRequirement {
  label: string;
  check: (password: string) => boolean;
}

const passwordRequirements: PasswordRequirement[] = [
  { label: 'At least 8 characters', check: (p) => p.length >= 8 },
  { label: 'Contains a number', check: (p) => /\d/.test(p) },
  { label: 'Contains uppercase letter', check: (p) => /[A-Z]/.test(p) },
  { label: 'Contains lowercase letter', check: (p) => /[a-z]/.test(p) },
];

export default function ResetPasswordScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { alert, error: showError } = useAlert();
  const { updatePassword, isConfigured, isPasswordRecovery, endPasswordRecovery } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [screenState, setScreenState] = useState<ScreenState>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  // Gate the form on a GENUINE password-recovery state (SEC-AUTH-03).
  //
  // This previously unlocked on `if (session)` — the presence of ANY session.
  // That made /reset-password a second, weaker path to "change this account's
  // password": an ordinary signed-in user (or an attacker on a hijacked AAL1
  // session) could navigate here directly and skip change-password.tsx's AAL2
  // step-up and current-password check entirely.
  //
  // `isPasswordRecovery` is set ONLY by Supabase's `PASSWORD_RECOVERY` auth
  // event, which fires only when a real recovery link is consumed. An ordinary
  // session never sets it, so direct navigation now lands on the error state.
  useEffect(() => {
    // Demo / unconfigured mode has no real auth events — keep the form
    // reachable for design and QA work, exactly as before.
    if (!isConfigured || !isSupabaseConfigured()) {
      setScreenState('input');
      return;
    }

    // The event is asynchronous: on web, supabase-js parses the URL fragment
    // and emits PASSWORD_RECOVERY shortly AFTER this screen mounts. So we wait
    // a bounded grace period rather than deciding on the first render — but we
    // unlock the instant the flag flips, so the legitimate flow feels instant.
    if (isPasswordRecovery) {
      setScreenState('input');
      return;
    }

    const timer = setTimeout(() => {
      // Still no recovery event → this was not a recovery entry. Do NOT fall
      // back to "there is a session, close enough".
      setErrorMessage('Invalid or expired reset link. Please request a new one.');
      setScreenState('error');
    }, RECOVERY_EVENT_GRACE_MS);

    return () => clearTimeout(timer);
  }, [isConfigured, isPasswordRecovery]);

  const allRequirementsMet = passwordRequirements.every((req) => req.check(password));
  const passwordsMatch = password === confirmPassword && password.length > 0;

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (isConfigured && isSupabaseConfigured()) {
        const { error } = await updatePassword(password);
        if (error) throw error;
      } else {
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    },
    onSuccess: () => {
      // Spend the capability the moment it has been used. Without this the
      // window would stay open for the rest of its 15 minutes, leaving a live
      // "change this password again" route on the device.
      endPasswordRecovery();
      setScreenState('success');
    },
    onError: (error: any) => showError('Update Failed', getUserMessage(error)),
  });

  const isLoading = updateMutation.isPending;

  const handleUpdatePassword = () => {
    if (!allRequirementsMet) {
      alert('Weak Password', 'Please ensure your password meets all requirements.');
      return;
    }

    if (!passwordsMatch) {
      alert('Passwords Don\'t Match', 'Please ensure both passwords are the same.');
      return;
    }

    updateMutation.mutate();
  };

  const handleGoToLogin = () => {
    router.replace('/login');
  };

  const handleRequestNewLink = () => {
    router.replace('/forgot-password');
  };

  if (screenState === 'loading') {
    return (
      <AuthLayout>
        <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
          <ActivityIndicator size="large" color={colors.primary[600]} />
          <Text style={{ color: colors.text.secondary }} className="mt-4 text-base">
            Verifying reset link...
          </Text>
        </View>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <View className="flex-1" style={{ backgroundColor: isWeb ? colors.surface.primary : colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top', 'bottom']} className="flex-1">
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            className="flex-1"
          >
            <ScrollView
              className="flex-1"
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ flexGrow: 1, justifyContent: isWeb ? 'center' : 'flex-start' }}
              keyboardShouldPersistTaps="handled"
            >
              {screenState === 'error' && (
                <>
                  {/* Error State */}
                  <Animated.View
                    entering={FadeInDown.delay(100).duration(600)}
                    className={`items-center px-5 ${isWeb ? 'pt-4 pb-6' : 'pt-12 pb-8'}`}
                  >
                    <View
                      className="w-24 h-24 rounded-full items-center justify-center mb-6"
                      style={{ backgroundColor: colors.error[50] }}
                    >
                      <AlertCircle size={48} color={colors.error[600]} />
                    </View>
                    <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mb-3">
                      Link Expired
                    </Text>
                    <Text style={{ color: colors.text.secondary }} className="text-center text-base leading-relaxed px-4">
                      {errorMessage}
                    </Text>
                  </Animated.View>

                  <Animated.View
                    entering={FadeInUp.delay(200).duration(500)}
                    className="px-5 mt-4"
                  >
                    <AnimatedPressable
                      onPress={handleRequestNewLink}
                      className="rounded-xl py-4 flex-row items-center justify-center gap-2"
                      style={{ backgroundColor: colors.primary[600] }}
                    >
                      <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                        Request New Link
                      </Text>
                    </AnimatedPressable>
                  </Animated.View>

                  <Animated.View
                    entering={FadeInUp.delay(300).duration(500)}
                    className="items-center px-5 mt-6 pb-8"
                  >
                    <Pressable onPress={handleGoToLogin}>
                      <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                        Back to Sign In
                      </Text>
                    </Pressable>
                  </Animated.View>
                </>
              )}

              {screenState === 'input' && (
                <>
                  {/* Logo & Title */}
                  <Animated.View
                    entering={FadeInDown.delay(100).duration(500)}
                    className={`items-center px-5 ${isWeb ? 'pt-4 pb-6' : 'pt-8 pb-8'}`}
                  >
                    {!isWeb && (
                      <Image
                        source={LOGO_MARK}
                        className="w-20 h-20 mb-6"
                        resizeMode="contain"
                      />
                    )}
                    <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mb-2">
                      Create New Password
                    </Text>
                    <Text style={{ color: colors.text.secondary }} className="text-center text-base leading-relaxed px-4">
                      Your new password must be different from your previous password.
                    </Text>
                  </Animated.View>

                  {/* Password Input */}
                  <Animated.View
                    entering={FadeInUp.delay(200).duration(500)}
                    className="px-5"
                  >
                    <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
                      New Password
                    </Text>
                    <View
                      className="flex-row items-center rounded-xl px-4"
                      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                    >
                      <Lock size={20} color={colors.text.tertiary} />
                      <TextInput
                        style={{ color: colors.text.primary }}
                        className="flex-1 py-4 pl-3 text-base"
                        placeholder="Enter new password"
                        placeholderTextColor={colors.text.tertiary}
                        value={password}
                        onChangeText={setPassword}
                        secureTextEntry={!showPassword}
                        autoCapitalize="none"
                      />
                      <Pressable onPress={() => setShowPassword(!showPassword)} className="p-2">
                        {showPassword ? (
                          <EyeOff size={20} color={colors.text.tertiary} />
                        ) : (
                          <Eye size={20} color={colors.text.tertiary} />
                        )}
                      </Pressable>
                    </View>

                    {/* Password Requirements */}
                    <View className="mt-4 gap-2">
                      {passwordRequirements.map((req, index) => {
                        const met = req.check(password);
                        return (
                          <View key={index} className="flex-row items-center gap-2">
                            <View
                              className="w-5 h-5 rounded-full items-center justify-center"
                              style={{ backgroundColor: met ? colors.success[100] : colors.border.light }}
                            >
                              {met && <Check size={12} color={colors.success[600]} />}
                            </View>
                            <Text
                              style={{ color: met ? colors.success[600] : colors.text.tertiary }}
                              className="text-sm"
                            >
                              {req.label}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  </Animated.View>

                  {/* Confirm Password Input */}
                  <Animated.View
                    entering={FadeInUp.delay(300).duration(500)}
                    className="px-5 mt-4"
                  >
                    <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
                      Confirm Password
                    </Text>
                    <View
                      className="flex-row items-center rounded-xl px-4"
                      style={{
                        backgroundColor: colors.surface.primary,
                        borderWidth: 1,
                        borderColor: confirmPassword && !passwordsMatch ? colors.error[400] : colors.border.light,
                      }}
                    >
                      <Lock size={20} color={colors.text.tertiary} />
                      <TextInput
                        style={{ color: colors.text.primary }}
                        className="flex-1 py-4 pl-3 text-base"
                        placeholder="Confirm new password"
                        placeholderTextColor={colors.text.tertiary}
                        value={confirmPassword}
                        onChangeText={setConfirmPassword}
                        secureTextEntry={!showConfirmPassword}
                        autoCapitalize="none"
                      />
                      <Pressable onPress={() => setShowConfirmPassword(!showConfirmPassword)} className="p-2">
                        {showConfirmPassword ? (
                          <EyeOff size={20} color={colors.text.tertiary} />
                        ) : (
                          <Eye size={20} color={colors.text.tertiary} />
                        )}
                      </Pressable>
                    </View>
                    {confirmPassword && !passwordsMatch && (
                      <Text style={{ color: colors.error[500] }} className="text-xs mt-2">
                        Passwords do not match
                      </Text>
                    )}
                  </Animated.View>

                  {/* Submit Button */}
                  <Animated.View
                    entering={FadeInUp.delay(400).duration(500)}
                    className="px-5 mt-8"
                  >
                    <AnimatedPressable
                      onPress={handleUpdatePassword}
                      disabled={isLoading || !allRequirementsMet || !passwordsMatch}
                      className="rounded-xl py-4 flex-row items-center justify-center gap-2"
                      style={{
                        backgroundColor: isLoading || !allRequirementsMet || !passwordsMatch
                          ? colors.border.medium
                          : colors.primary[600],
                      }}
                    >
                      <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                        {isLoading ? 'Updating...' : 'Update Password'}
                      </Text>
                    </AnimatedPressable>
                  </Animated.View>
                </>
              )}

              {screenState === 'success' && (
                <>
                  {/* Success State */}
                  <Animated.View
                    entering={FadeInDown.delay(100).duration(600)}
                    className={`items-center px-5 ${isWeb ? 'pt-4 pb-6' : 'pt-12 pb-8'}`}
                  >
                    <View
                      className="w-24 h-24 rounded-full items-center justify-center mb-6"
                      style={{ backgroundColor: colors.success[50] }}
                    >
                      <CheckCircle size={48} color={colors.success[600]} />
                    </View>
                    <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mb-3">
                      Password Updated
                    </Text>
                    <Text style={{ color: colors.text.secondary }} className="text-center text-base leading-relaxed px-4">
                      Your password has been successfully updated. You can now sign in with your new password.
                    </Text>
                  </Animated.View>

                  <Animated.View
                    entering={FadeInUp.delay(200).duration(500)}
                    className="px-5 mt-4"
                  >
                    <AnimatedPressable
                      onPress={handleGoToLogin}
                      className="rounded-xl py-4 flex-row items-center justify-center gap-2"
                      style={{ backgroundColor: colors.primary[600] }}
                    >
                      <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                        Go to Sign In
                      </Text>
                    </AnimatedPressable>
                  </Animated.View>
                </>
              )}
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </View>
    </AuthLayout>
  );
}
